<template>
  <div id="app">
    <div v-if="env !== 'production'" class="env">Você está no ambiente {{env && env.toUpperCase()}}</div>
    <router-view v-if="load"/>
    <template v-else>
      <div class="lds-roller">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </template>

    <Loading :load="getLoad"/>

    <GlobalNotify/>
    <RedMine v-if="!getPartner"/>
  </div>
</template>

<script>
import GetUser from '@/mixins/GetUser'
import { onSessionChangedElsewhere } from '@/utils/sessionChannel'
import GetPartner from '@/mixins/GetPartner'
import GlobalLoading from '@/mixins/GlobalLoading'
import GlobalNotify from '@/components/shares/GlobalNotify'
import Loading from '@/components/shares/Loading'
import RedMine from '@/components/help-desk/RedMine.vue'

const expired_in = process.env.VUE_APP_ENV === 'production' ? 12 * 60 : 1
const stylerPartner = [
  'primary_color',
  'primary_color_hover',
  'primary_color_active',
  'primary_color_disabled',
  'login_color',
]

const createFavicon = (document, favicon) => {
  const link = document.createElement('link')
  link.rel = 'icon'
  link.href = '/' + favicon
  document.head.appendChild(link)
}

const brobotIcon = (document) => createFavicon(document, 'favbot-300x300.png')
const defaultIcon = (document) => createFavicon(document, 'favicon.png')

export default {
  components: { GlobalNotify, Loading, RedMine },
  async created() {
    onSessionChangedElsewhere()

    const partner = await this.loadPartner()

    if (partner)
      this.setStylerPartner(partner)
    else
      this.setDefaultStyler()

    if (this.authenticated() || this.resetPasswordPage()) {
      this.load = true
      return
    }

    const userData = this.$ls.get('userAuthenticated', { withoutKey: true })
    if (userData) {
      this.setUser(userData)
      this.load = true
      return 
    }

    this.axios.get('auth/validate_token')
      .then(response => { 
        this.setUser(response.data)
        this.$ls.set('userAuthenticated', response.data, { minutes: 10, withoutKey: true })
      })
      .catch(e => this.$notify.addError(e))
      .finally(() => this.load = true)
  },
  data() {
    return {
      load: false,
      env: process.env.VUE_APP_ENV,
    }
  },
  methods: {
    async loadPartner() {
      const domain = location.hostname
      const partner = this.$ls.get(`partnerDomain=${domain}`, { withoutKey: true }) ||
        await this.fetchPartner(domain)

      this.setPartner(partner)
      return partner
    },
    async fetchPartner(domain) {
      try {
        const result = await this.axios.get(`/accounts/${domain}/partner`)
        const partner = result.data
        this.$ls.set(`partnerDomain=${domain}`, partner, { minutes: expired_in, withoutKey: true })
        return partner
      } catch (e) {
        console.log(e)
      }
    },
    setStylerPartner(partner) {
      stylerPartner.forEach(attribute => {
        const value = partner.style[attribute]
        if (value)
          document.documentElement.style.setProperty(`--${attribute.replace(/_/g, '-')}`, value)
      })
      defaultIcon(document)
      document.title = partner.name
    },
    setDefaultStyler() {
      brobotIcon(document)
      document.title = 'Brobot Platform'
    },
    authenticated() {
      return !localStorage.authHeaders
    },
    resetPasswordPage() {
      return location.pathname === '/reset_password'
    },
  },
  mounted() {
    // Initialize theme after Vue is fully mounted
    this.$store.commit('initTheme')
  },
  mixins: [GetUser, GetPartner, GlobalLoading],
  name: 'App'
}
</script>

<style lang="scss">
  @import "./assets/main.scss";

  .env {
    display: flex;
    justify-content: center;
    background-color: $secondary-color;
    color: white;
  }

  .load-app {
    position: fixed;
    top: 45%;
    left: 45%;
  }

  .lds-roller {
    display: inline-block;
    position: fixed;
    width: 100%;
    height: 100%;
    top: 40%;
    left: 45%;
  }
  .lds-roller div {
    animation: lds-roller 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    transform-origin: 40px 40px;
  }
  .lds-roller div:after {
    content: " ";
    display: block;
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fff;
    margin: -50px 0 0 -50px;
  }
  .lds-roller div:nth-child(1) {
    animation-delay: -0.036s;
  }
  .lds-roller div:nth-child(1):after {
    top: calc(50% * 63px);
    left: calc(50% * 63px);
  }
  .lds-roller div:nth-child(2) {
    animation-delay: -0.072s;
  }
  .lds-roller div:nth-child(2):after {
    top: calc(50% * 68px);
    left: calc(50% * 56px);
  }
  .lds-roller div:nth-child(3) {
    animation-delay: -0.108s;
  }
  .lds-roller div:nth-child(3):after {
    top: calc(50% * 71px);
    left: calc(50% * 48px);
  }
  .lds-roller div:nth-child(4) {
    animation-delay: -0.144s;
  }
  .lds-roller div:nth-child(4):after {
    top: calc(50% * 72px);
    left: calc(50% * 40px);
  }
  .lds-roller div:nth-child(5) {
    animation-delay: -0.18s;
  }
  .lds-roller div:nth-child(5):after {
    top: calc(50% * 71px);
    left: calc(50% * 32px);
  }
  .lds-roller div:nth-child(6) {
    animation-delay: -0.216s;
  }
  .lds-roller div:nth-child(6):after {
    top: calc(50% * 68px);
    left: calc(50% * 24px);
  }
  .lds-roller div:nth-child(7) {
    animation-delay: -0.252s;
  }
  .lds-roller div:nth-child(7):after {
    top: calc(50% * 63px);
    left: calc(50% * 17px);
  }
  .lds-roller div:nth-child(8) {
    animation-delay: -0.288s;
  }
  .lds-roller div:nth-child(8):after {
    top: calc(50% * 56px);
    left: calc(50% * 12px);
  }
  @keyframes lds-roller {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

</style>
